import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig, TourStep } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";

export const useDrawingAnalysisTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const {
    setHighlightedItemKey,
    setActiveTourModule,
    setCurrentTourStep,
    hoverTooltipsEnabled,
  } = useTourGuideContext();
  const { suggestedQuestionsData, faqsData } = useMainChatContext();

  // Tour guide refs
  const textareaRef = useRef<HTMLDivElement>(null);
  const suggestedQuestionsRef = useRef<HTMLDivElement>(null);

  const isDrawingAnalysis =
    pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD ||
    pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT);

  // Check if step 3 should be included (if suggested questions or FAQs exist)
  const hasSuggestedQuestionsOrFaqs =
    (suggestedQuestionsData?.rows &&
      suggestedQuestionsData?.rows?.length > 0) ||
    (faqsData?.rows && faqsData?.rows?.length > 0);

  // Tour guide configuration for drawing analysis chatbot
  const drawingAnalysisTourConfig: TourGuideConfig = useMemo(() => {
    if (!isDrawingAnalysis) {
      return {
        moduleId: "drawingAnalysis",
        steps: [],
        enabled: false,
      };
    }

    const steps: TourStep[] = [
      {
        id: "drawing-textarea",
        targetRef: textareaRef,
        title:
          t("tourGuide.drawingAnalysis.step1.title") || "Ask About Your PDFs",
        description:
          t("tourGuide.drawingAnalysis.step1.description") ||
          "Ask questions about specifications, dimensions, or technical details in your uploaded PDF",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
      {
        id: "drawing-chatbot-dropdown",
        targetSelector: '[data-tour-item="project-chatbot-dropdown"]',
        title:
          t("tourGuide.drawingAnalysis.step2.title") ||
          "Upload File or Switch Modes",
        description:
          t("tourGuide.drawingAnalysis.step2.description") ||
          "Click the plus icon to upload PDF drawing file or switch between different chat modes",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
    ];

    // Add step 3 (suggested questions) only if data exists
    if (hasSuggestedQuestionsOrFaqs) {
      steps.push({
        id: "drawing-suggested-questions",
        targetRef: suggestedQuestionsRef,
        title:
          t("tourGuide.drawingAnalysis.step3.title") || "Suggested Questions",
        description:
          t("tourGuide.drawingAnalysis.step3.description") ||
          "Browse suggested questions and FAQs to get started quickly. Click on any question to use it as your query.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      });
    }

    return {
      moduleId: "drawingAnalysis",
      enabled: true,
      steps,
    };
  }, [isDrawingAnalysis, t, hasSuggestedQuestionsOrFaqs]);

  const baseTourGuide = useTourGuide(drawingAnalysisTourConfig);
  
  // Auto-start tour guide and handle completion tracking
  const drawingAnalysisTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "drawing_analysis_tour_completed",
    isOnTourPath: isDrawingAnalysis,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (drawingAnalysisTourGuide.isActive) {
      setActiveTourModule("drawingAnalysis");
      setCurrentTourStep(drawingAnalysisTourGuide.currentStep);
      setHighlightedItemKey(null);
    } else {
      setActiveTourModule(null);
      setCurrentTourStep(0);
      setHighlightedItemKey(null);
    }
  }, [
    drawingAnalysisTourGuide.isActive,
    drawingAnalysisTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for drawing analysis tour guide
  const textareaStep = drawingAnalysisTourConfig.steps.find(
    (step) => step.id === "drawing-textarea"
  );
  const chatbotDropdownStep = drawingAnalysisTourConfig.steps.find(
    (step) => step.id === "drawing-chatbot-dropdown"
  );
  const suggestedQuestionsStep = drawingAnalysisTourConfig.steps.find(
    (step) => step.id === "drawing-suggested-questions"
  );

  const textareaHover = useTourGuideHover(
    "drawing-textarea",
    textareaStep || null
  );
  const chatbotDropdownHover = useTourGuideHover(
    "drawing-chatbot-dropdown",
    chatbotDropdownStep || null
  );
  const suggestedQuestionsHover = useTourGuideHover(
    "drawing-suggested-questions",
    suggestedQuestionsStep || null
  );

  // Attach hover handlers to chatbot dropdown via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      drawingAnalysisTourGuide.isActive ||
      !chatbotDropdownStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="project-chatbot-dropdown"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!chatbotDropdownHover.tooltipData) return;
        chatbotDropdownHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        chatbotDropdownHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    drawingAnalysisTourGuide.isActive,
    chatbotDropdownStep,
    chatbotDropdownHover.hoverProps,
    chatbotDropdownHover.tooltipData,
  ]);

  return {
    textareaRef,
    suggestedQuestionsRef,
    drawingAnalysisTourGuide,
    drawingAnalysisTourConfig,
    isDrawingAnalysis,
    textareaHover,
    chatbotDropdownHover,
    suggestedQuestionsHover,
  };
};
